import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import type { ProviderDetail } from '../../../../../js/api';
import { ProvidersService, SourceTypeEnum } from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Badge } from '../../../components/badge';
import { DrawerCloseButton } from '../../../components/drawer';
import type { CodeDefinitionOption } from '../../../components/dropdown-select';
import { DropdownSelect } from '../../../components/dropdown-select';
import { StatusPill } from '../../../components/StatusPill';
import { Tabs, TabsButton, TabsContent, TabsList } from '../../../components/tabs';
import { WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useCollapseOnOverflow } from '../../../hooks/useCollapseOnOverflow';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { CredentialsTab } from './CredentialsTab';
import { EducationTab } from './EducationTab';
import { LicensesTab } from './LicensesTab';
import { OtherTab } from './OtherTab';
import { ProfessionalTraitsTab } from './ProfessionalTraitsTab';
import { ProviderActionsMenu } from './ProviderActionsMenu';
import { labelFor, PROVIDER_DRAWER_CATEGORIES, ProviderDrawerMaps } from './providerDisplay';
import { ProviderProfileSummary } from './ProviderProfileSummary';
import { ProviderQuickDetails } from './ProviderQuickDetails';
import { STAFF_PROVIDER_QUERY_KEY } from './queryKeys';

/** Tab codes double as the `tab` search param value. */
export const PROVIDER_DRAWER_TABS = [
  { code: 'credentials', label: 'Credentials' },
  { code: 'professional-traits', label: 'Professional Traits' },
  { code: 'education', label: 'Education' },
  { code: 'licenses', label: 'Licenses & Certifications' },
  { code: 'other', label: 'Other' },
] as const;

type ProviderDrawerProps = {
  providerId: string;
  onClose?: () => void;
  /** Hide the popout button (e.g. when already on the full-page route). */
  isFullPage?: boolean;
};

/** Full-page route for a provider; the drawer's popout button and the connection drawer link here. */
export const staffProviderDetailPath = (providerId: string, tab?: string | null) => {
  const tabParam = tab ? `?tab=${tab}` : '';
  return `/staff/providers/${providerId}${tabParam}`;
};

/**
 * Read-only staff view of a provider: what the legacy Bootstrap profile page
 * showed (header info on top, the old profile tabs below), minus document
 * downloads — the legacy provider document models are being replaced by the
 * documents framework, so only the DATA of those records is shown.
 */
export function ProviderDrawer({ providerId, onClose, isFullPage = false }: ProviderDrawerProps) {
  const { user } = useContext(SessionContext);
  // Django admin flag (not app-level staff): gates the admin link only.
  const showAdminLink = Boolean(user?.is_staff);
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);
  // The tab lives in `?tab=` (default tab omitted) like the assignment/timecard/facility
  // drawers: deep-linkable, and sticky across providers because the list only swaps `id`.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTab = PROVIDER_DRAWER_TABS[0].code;
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState<string>(
    tabFromUrl && PROVIDER_DRAWER_TABS.some((t) => t.code === tabFromUrl) ? tabFromUrl : defaultTab
  );
  const setActiveTab = useCallback(
    (tab: string) => {
      setActiveTabState(tab);
      const next = new URLSearchParams(searchParams);
      if (tab && tab !== defaultTab) {
        next.set('tab', tab);
      } else {
        next.delete('tab');
      }
      // Keep the history state so the full page's referrer survives tab changes.
      setSearchParams(next, { replace: true, state: location.state });
    },
    [searchParams, setSearchParams, defaultTab, location.state]
  );

  const {
    data: provider,
    isLoading,
    error,
  } = useQuery<ProviderDetail>({
    queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId],
    queryFn: () => ProvidersService.providersRetrieve({ id: providerId }),
    enabled: !!providerId,
  });

  const tabs = useMemo(() => [...PROVIDER_DRAWER_TABS], []);
  const { ref: tabsMeasureRef, collapsed: isOverflowCollapsed } =
    useCollapseOnOverflow<HTMLDivElement>({
      observeChildren: true,
      forceCollapse: isMobile,
      constraintRef: drawerRef,
      // The tabs only mount once the provider has loaded; flipping the id then makes the
      // hook (re)install its observers against the mounted strip.
      recheckOn: { id: provider ? providerId : '', tabs },
    });

  // A profile picture that fails to load (missing object, expired signed URL) falls back
  // to the placeholder icon instead of a broken image.
  const [pictureFailed, setPictureFailed] = useState(false);
  useEffect(() => {
    setPictureFailed(false);
  }, [providerId]);

  useDocumentTitle(provider?.full_name ?? '', false, isFullPage && !!provider?.full_name);

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps(PROVIDER_DRAWER_CATEGORIES);
  const maps: ProviderDrawerMaps = codeDefinitionMaps || {};

  if (isLoading) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8" data-testid="provider-drawer-loading">
        <div className="tw:text-zinc-500">Loading provider details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8" data-testid="provider-drawer-error">
        <div className="tw:text-stoplight-danger">
          Error loading provider details. Please try again.
        </div>
      </div>
    );
  }

  if (!provider) {
    return null;
  }

  const subtitle = [provider.public_name].filter(Boolean).join(' · ');

  return (
    <div ref={drawerRef} className="tw:flex tw:flex-col tw:mt-6" data-testid="provider-drawer">
      {/* Top Section - Provider Info, Status, Close */}
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:mb-4">
        {provider.profile_picture && !pictureFailed ? (
          <span className="tw:inline-block tw:size-12 tw:shrink-0 tw:overflow-hidden tw:rounded-lg tw:outline-1 tw:-outline-offset-1 tw:outline-zinc-300 tw:md:order-1">
            <img
              src={provider.profile_picture}
              alt={provider.full_name}
              className="tw:size-full tw:object-cover"
              onError={() => setPictureFailed(true)}
              data-testid="provider-drawer-picture"
            />
          </span>
        ) : (
          <div
            className="tw:hidden tw:md:flex tw:items-center tw:justify-center tw:size-12 tw:shrink-0 tw:bg-brand-green-1 tw:text-white tw:rounded-full tw:md:order-1"
            data-testid="provider-drawer-picture-fallback"
          >
            <i className="fa-solid fa-user-doctor tw:text-2xl" />
          </div>
        )}
        <div className="tw:flex tw:flex-col tw:gap-0.5 tw:flex-1 tw:min-w-0 tw:order-2">
          <div className="tw:flex tw:items-center tw:gap-2 tw:min-w-0">
            <span
              className="tw:text-base tw:md:text-xl tw:font-bold tw:truncate"
              data-testid="provider-drawer-name"
            >
              {provider.full_name || provider.display_name}
            </span>
            {provider.source_type === SourceTypeEnum.ORGANIZATION && (
              <span className={SOURCE_TYPE_ORGANIZATION_BADGE_STYLE}>1099</span>
            )}
          </div>
          {subtitle && (
            <span
              className="tw:text-sm tw:text-zinc-500 tw:truncate"
              data-testid="provider-drawer-subtitle"
            >
              {subtitle}
            </span>
          )}
        </div>
        <div className="tw:order-4 tw:w-full tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <StatusPill scope={WorkflowScopes.Provider} status={provider.current_status} />
          <Badge color="zinc">
            {labelFor(provider.registration_status, maps.RegistrationStatus)}
          </Badge>
          {provider.profile_disposition && (
            <Badge color="zinc">
              {labelFor(provider.profile_disposition, maps.ProfileDisposition)}
            </Badge>
          )}
        </div>
        {/* Actions menu + Popout + Close - right side of row 1 */}
        <div className="tw:order-3">
          <ProviderActionsMenu provider={provider} showAdminLink={showAdminLink} />
        </div>
        {!isFullPage && !isMobile && (
          <button
            type="button"
            className="tw:order-3 tw:text-zinc-400 tw:hover:text-zinc-600 tw:p-1"
            onClick={() =>
              navigate(
                staffProviderDetailPath(providerId, activeTab !== defaultTab ? activeTab : null),
                {
                  state: { referrer: location.pathname + location.search },
                }
              )
            }
            title="Open full page"
            data-testid="provider-drawer-popout"
          >
            <i className="fas fa-up-right-from-square" />
          </button>
        )}
        {onClose && (
          <DrawerCloseButton
            onClick={onClose}
            className="tw:order-3"
            data-testid="provider-drawer-close"
          />
        )}
      </div>

      <ProviderQuickDetails provider={provider} maps={maps} />

      <ProviderProfileSummary provider={provider} maps={maps} />

      <Tabs activeTab={activeTab} onChange={setActiveTab} className="tw:mt-6">
        <div className="tw:flex tw:flex-col tw:w-full">
          {isOverflowCollapsed ? (
            <div className="tw:pb-2">
              <div className="tw:text-xs tw:text-zinc-500 tw:mb-1">Select View</div>
              <DropdownSelect
                inputId="provider-drawer-section"
                aria-label="Provider section"
                allOptions={tabs.map((t) => ({ value: t.code, label: t.label }))}
                value={activeTab}
                isSearchable={false}
                isClearable={false}
                onChange={(opt: CodeDefinitionOption | CodeDefinitionOption[] | null) => {
                  if (opt && !Array.isArray(opt)) setActiveTab(opt.value);
                }}
              />
            </div>
          ) : null}
          <div
            aria-hidden={isOverflowCollapsed}
            ref={tabsMeasureRef}
            className={twMerge(
              'tw:w-full',
              isOverflowCollapsed &&
                'tw:invisible tw:max-h-0 tw:overflow-hidden tw:pointer-events-none'
            )}
          >
            <TabsList className={isOverflowCollapsed ? 'tw:border-transparent' : undefined}>
              {tabs.map((t) => (
                <TabsButton key={t.code} tab={t.code} dataTestId={`provider-tab-${t.code}`}>
                  {t.label}
                </TabsButton>
              ))}
            </TabsList>
          </div>
        </div>

        <TabsContent tab="credentials">
          <CredentialsTab provider={provider} maps={maps} />
        </TabsContent>
        <TabsContent tab="professional-traits">
          <ProfessionalTraitsTab provider={provider} maps={maps} />
        </TabsContent>
        <TabsContent tab="education">
          <EducationTab provider={provider} maps={maps} />
        </TabsContent>
        <TabsContent tab="licenses">
          <LicensesTab provider={provider} maps={maps} />
        </TabsContent>
        <TabsContent tab="other">
          <OtherTab provider={provider} maps={maps} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
